import { useState, type FormEvent } from "react";
import { ArrowLeft, Plus } from "lucide-react";
import {
  actions,
  listBusinessUnits,
  listDirectPurchasePolicies,
  listLocations,
  listPurchaseOrderMessages,
  listPurchaseOrders,
  listRequisitions,
  listSupplierFulfilmentUpdates,
  listVendors,
  recordFulfilmentEvent,
} from "@/lib/api";
import type {
  BusinessUnit,
  DirectPurchasePolicy,
  Location,
  PurchaseOrder,
  PurchaseOrderMessage,
  Requisition,
  SupplierFulfilmentUpdate,
  Vendor,
} from "@/lib/types";
import { displayDate, money, titleCase } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useData, matches, reportActionError, localDateTimeInputValue, type View, type ViewProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";
import { AppLink } from "@/components/app-link";
import { ControlPassportPanel } from "@/components/control-passport-panel";

export function Orders({
  organisation,
  refresh,
  query,
  recordId,
  can,
  changed,
  navigate,
}: ViewProps & {
  recordId: string;
  navigate: (view: View, recordId?: string) => void;
}) {
  const { data, error, loading } = useData(async () => {
    const [
      orders,
      messages,
      supplierFulfilment,
      policies,
      requisitions,
      vendors,
      units,
      locations,
    ] = await Promise.all([
      listPurchaseOrders(organisation.id),
      listPurchaseOrderMessages(organisation.id),
      listSupplierFulfilmentUpdates(organisation.id),
      listDirectPurchasePolicies(organisation.id),
      listRequisitions(organisation.id, "approved"),
      listVendors(organisation.id),
      listBusinessUnits(organisation.id),
      listLocations(organisation.id),
    ]);
    return {
      orders,
      messages,
      supplierFulfilment,
      policies,
      requisitions,
      vendors,
      units,
      locations,
    };
  }, [organisation.id, refresh]);
  const [receipt, setReceipt] = useState<PurchaseOrder>();
  const [fulfilment, setFulfilment] = useState<PurchaseOrder>();
  const [invoice, setInvoice] = useState<PurchaseOrder>();
  const [directOpen, setDirectOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [busy, setBusy] = useState("");
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data.orders.filter((row) =>
    matches(
      query,
      row.id,
      row.po_no,
      row.vendors?.name,
      row.rfqs?.rfq_no,
      row.status,
    ),
  );
  async function issue(id: string) {
    setBusy(id);
    try {
      await actions.issuePurchaseOrder(organisation.id, id);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }
  if (recordId) {
    const order = data.orders.find((row) => row.id === recordId);
    if (!order)
      return (
        <PageHeader
          eyebrow="SOURCE ORDER"
          title="Purchase order unavailable"
          description="This order is not available in the current organisation."
        >
          <Button variant="outline" asChild>
            <AppLink view="orders" navigate={navigate}>
              <ArrowLeft className="size-4" aria-hidden="true" />
              All purchase orders
            </AppLink>
          </Button>
        </PageHeader>
      );
    return (
      <>
        <OrderRecord
          row={order}
          organisationId={organisation.id}
          busy={busy}
          can={can}
          navigate={navigate}
          onIssue={issue}
          onReceipt={setReceipt}
          onFulfilment={setFulfilment}
          onInvoice={setInvoice}
        />
        <ReceiptDialog
          purchaseOrder={receipt}
          onOpenChange={(open) => !open && setReceipt(undefined)}
          organisationId={organisation.id}
          onDone={changed}
        />
        <FulfilmentDialog
          purchaseOrder={fulfilment}
          onOpenChange={(open) => !open && setFulfilment(undefined)}
          organisationId={organisation.id}
          onDone={changed}
        />
        <InvoiceDialog
          purchaseOrder={invoice}
          onOpenChange={(open) => !open && setInvoice(undefined)}
          organisationId={organisation.id}
          onDone={() => {
            changed();
            navigate("invoices");
          }}
        />
      </>
    );
  }
  return (
    <>
      <PageHeader
        eyebrow="ORDER"
        title="Purchase orders"
        description="Issue commitments and receive against approved order lines."
      >
        {(can("manage") || can("finance")) && (
          <Button className="min-h-11" variant="outline" onClick={() => setPolicyOpen(true)}>
            Direct PO policy
          </Button>
        )}
        {can("procure") && (
          <Button className="min-h-11" onClick={() => setDirectOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Direct or external PO
          </Button>
        )}
      </PageHeader>
      {rows.length ? (
        <>
          <div className="hidden md:block">
            <Table
              heads={[
                "Purchase order",
                "Vendor",
                "Value",
                "Receipt progress",
                "Status",
                "Action",
              ]}
            >
              {rows.map((row) => {
                const { ordered, received } = receiptProgress(row);
                return (
                  <tr key={row.id}>
                    <td>
                      <AppLink
                        view="orders"
                        recordId={row.id}
                        navigate={navigate}
                        ariaLabel={`Open purchase order ${row.po_no}`}
                        className="inline-block"
                      >
                        <strong>{row.po_no}</strong>
                        <small className="block text-[var(--text-secondary)]">
                          {orderSource(row)}
                        </small>
                      </AppLink>
                    </td>
                    <td>{row.vendors?.name}</td>
                    <td className="numeric">{money(row.total)}</td>
                    <td>
                      <span className="tabular-nums">{received} / {ordered}</span>
                      <small className="block text-[var(--text-secondary)]">
                        accepted / ordered
                      </small>
                    </td>
                    <td>
                      <Badge value={row.status} />
                    </td>
                    <td>
                      <OrderActions
                        row={row}
                        busy={busy}
                        can={can}
                        onIssue={issue}
                        onReceipt={setReceipt}
                        onFulfilment={setFulfilment}
                        onInvoice={setInvoice}
                      />
                    </td>
                  </tr>
                );
              })}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Purchase orders">
            {rows.map((row) => {
              const { ordered, received } = receiptProgress(row);
              return (
                <article
                  key={row.id}
                  className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <AppLink
                        view="orders"
                        recordId={row.id}
                        navigate={navigate}
                        ariaLabel={`Open purchase order ${row.po_no}`}
                        className="block"
                      >
                        <span className="block text-xs font-bold tracking-[.08em] text-[var(--text-muted)]">
                          {row.po_no}
                        </span>
                        <span className="mt-1 block text-base font-semibold">{row.vendors?.name}</span>
                        <span className="mt-1 block text-xs text-[var(--text-secondary)]">
                          {orderSource(row)}
                        </span>
                      </AppLink>
                    </div>
                    <Badge value={row.status} />
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <dt className="text-xs text-[var(--text-muted)]">Order value</dt>
                      <dd className="mt-0.5 font-semibold tabular-nums">{money(row.total)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-[var(--text-muted)]">Receipt progress</dt>
                      <dd className="mt-0.5 font-semibold tabular-nums">
                        {received} / {ordered}
                      </dd>
                      <dd className="mt-0.5 text-xs text-[var(--text-secondary)]">
                        accepted / ordered
                      </dd>
                    </div>
                  </dl>
                  <div className="mt-4 border-t border-[var(--border)] pt-4">
                    <OrderActions
                      row={row}
                      busy={busy}
                      can={can}
                      onIssue={issue}
                      onReceipt={setReceipt}
                      onFulfilment={setFulfilment}
                      onInvoice={setInvoice}
                      mobile
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </>
      ) : (
        <Empty
          title="No purchase orders yet"
          description="An awarded quotation creates a draft purchase order."
        />
      )}
      <PurchaseOrderCollaboration
        organisationId={organisation.id}
        orders={data.orders}
        messages={data.messages}
        fulfilment={data.supplierFulfilment}
        canReceive={can("receive")}
        onDone={changed}
      />
      <ReceiptDialog
        purchaseOrder={receipt}
        onOpenChange={(open) => !open && setReceipt(undefined)}
        organisationId={organisation.id}
        onDone={changed}
      />
      <FulfilmentDialog
        purchaseOrder={fulfilment}
        onOpenChange={(open) => !open && setFulfilment(undefined)}
        organisationId={organisation.id}
        onDone={changed}
      />
      <InvoiceDialog
        purchaseOrder={invoice}
        onOpenChange={(open) => !open && setInvoice(undefined)}
        organisationId={organisation.id}
        onDone={() => {
          changed();
          navigate("invoices");
        }}
      />
      <DirectPurchaseDialog
        open={directOpen}
        onOpenChange={setDirectOpen}
        organisationId={organisation.id}
        requisitions={data.requisitions}
        vendors={data.vendors}
        policies={data.policies}
        onDone={changed}
      />
      <DirectPurchasePolicyDialog
        open={policyOpen}
        onOpenChange={setPolicyOpen}
        organisationId={organisation.id}
        policies={data.policies}
        units={data.units}
        locations={data.locations}
        onDone={changed}
      />
    </>
  );
}

function OrderRecord({
  row,
  organisationId,
  busy,
  can,
  navigate,
  onIssue,
  onReceipt,
  onFulfilment,
  onInvoice,
}: {
  row: PurchaseOrder;
  organisationId: string;
  busy: string;
  can: ViewProps["can"];
  navigate: (view: View, recordId?: string) => void;
  onIssue: (id: string) => void;
  onReceipt: (row: PurchaseOrder) => void;
  onFulfilment: (row: PurchaseOrder) => void;
  onInvoice: (row: PurchaseOrder) => void;
}) {
  const { ordered, received } = receiptProgress(row);
  return (
    <>
      <PageHeader
        eyebrow="SOURCE ORDER"
        title={`${row.po_no} · ${row.vendors?.name || "Supplier"}`}
        description="Commercial commitment, source lineage and receipt progress."
      >
        <Button variant="outline" asChild>
          <AppLink view="orders" navigate={navigate}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            All purchase orders
          </AppLink>
        </Button>
      </PageHeader>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
        <div className="flex items-center gap-3">
          <Badge value={row.status} />
          <span className="text-sm text-[var(--text-secondary)]">
            {received} of {ordered} accepted
          </span>
        </div>
        <OrderActions
          row={row}
          busy={busy}
          can={can}
          onIssue={onIssue}
          onReceipt={onReceipt}
          onFulfilment={onFulfilment}
          onInvoice={onInvoice}
        />
      </div>
      <ControlPassportPanel
        organisationId={organisationId}
        checkpoint={["acknowledged", "partially_received", "received", "closed"].includes(row.status) ? "acknowledgement" : "purchase_order"}
        requestNo={(row as any).rfqs?.requisitions?.request_no}
        viewerRole={
          can("manage") ? "tenant administrator" :
          can("procure") ? "procurement" :
          can("receive") ? "receiver" :
          can("finance") ? "finance" :
          can("approve") ? "approver" : "requester"
        }
        capabilities={{
          manage: can("manage"),
          approve: can("approve"),
          procure: can("procure"),
          finance: can("finance"),
          receive: can("receive"),
        }}
        onNavigate={(view, recordId) => navigate(view as View, recordId)}
      />

      <div className="grid gap-5 lg:grid-cols-[.8fr_1.2fr]">
        <Panel>
          <p className="eyebrow">ORDER FACTS</p>
          <h2 className="mt-1 font-serif text-xl">Commitment</h2>
          <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Order value</dt>
              <dd className="mt-1 font-medium tabular-nums">{money(row.total, row.currency)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Procurement route</dt>
              <dd className="mt-1 font-medium">{titleCase(row.procurement_route)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">Issued</dt>
              <dd className="mt-1 font-medium">
                {row.issued_at ? displayDate(row.issued_at, true) : "Not issued"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-[var(--text-muted)]">External reference</dt>
              <dd className="mt-1 font-medium">{row.external_reference || "Not recorded"}</dd>
            </div>
          </dl>
          <div className="mt-5 rounded-lg border border-[var(--border)] p-3 text-sm">
            <span className="text-xs text-[var(--text-muted)]">Source record</span>
            <div className="mt-1 font-medium">
              {row.rfq_id ? (
                <AppLink view="rfqs" recordId={row.rfq_id} navigate={navigate}>
                  {row.rfqs?.rfq_no || "Open sourcing event"}
                </AppLink>
              ) : (
                <AppLink view="requisitions" recordId={row.requisition_id} navigate={navigate}>
                  Open source requisition
                </AppLink>
              )}
            </div>
          </div>
        </Panel>
        <Panel>
          <p className="eyebrow">ORDER LINES</p>
          <h2 className="mt-1 font-serif text-xl">Ordered and received</h2>
          <div className="mt-4 divide-y divide-[var(--border)] rounded-xl border border-[var(--border)]">
            {(row.po_lines || []).map((line) => (
              <article className="grid gap-3 p-4 sm:grid-cols-[1fr_auto]" key={line.id}>
                <div>
                  <strong className="text-sm">{line.description}</strong>
                  <p className="mt-1 text-xs text-[var(--text-secondary)]">
                    {line.quantity} {line.unit} ordered · {line.received_qty} accepted
                  </p>
                </div>
                <strong className="text-sm tabular-nums">
                  {money(Number(line.quantity) * Number(line.unit_price), row.currency)}
                </strong>
              </article>
            ))}
          </div>
        </Panel>
      </div>
    </>
  );
}

function receiptProgress(row: PurchaseOrder) {
  return {
    ordered:
      row.po_lines?.reduce((sum, line) => sum + Number(line.quantity), 0) || 0,
    received:
      row.po_lines?.reduce((sum, line) => sum + Number(line.received_qty), 0) || 0,
  };
}

function orderSource(row: PurchaseOrder) {
  return row.rfqs?.rfq_no
    ? `From ${row.rfqs.rfq_no}`
    : titleCase(row.procurement_route);
}

function OrderActions({
  row,
  busy,
  can,
  onIssue,
  onReceipt,
  onFulfilment,
  onInvoice,
  mobile = false,
}: {
  row: PurchaseOrder;
  busy: string;
  can: ViewProps["can"];
  onIssue: (id: string) => void;
  onReceipt: (row: PurchaseOrder) => void;
  onFulfilment: (row: PurchaseOrder) => void;
  onInvoice: (row: PurchaseOrder) => void;
  mobile?: boolean;
}) {
  return (
    <div className={`flex flex-wrap gap-2 ${mobile ? "[&>button]:min-h-11 [&>button]:flex-1" : ""}`}>
      {row.status === "draft" && can("procure") && (
        <Button
          size="sm"
          disabled={busy === row.id}
          onClick={() => onIssue(row.id)}
        >
          {busy === row.id ? "Issuing…" : "Issue PO"}
        </Button>
      )}
      {["issued", "part_received"].includes(row.status) && can("receive") && (
        <Button size="sm" onClick={() => onReceipt(row)}>
          Record receipt
        </Button>
      )}
      {["issued", "part_received", "received"].includes(row.status) &&
        can("receive") && (
          <Button size="sm" variant="outline" onClick={() => onFulfilment(row)}>
            Record fulfilment
          </Button>
        )}
      {["issued", "part_received", "received"].includes(row.status) &&
        can("finance") && (
          <Button size="sm" variant="outline" onClick={() => onInvoice(row)}>
            Record invoice
          </Button>
        )}
      {row.status === "received" && !can("finance") && (
        <span className="self-center text-xs text-[var(--text-muted)]">Complete</span>
      )}
    </div>
  );
}

function DirectPurchaseDialog({
  open,
  onOpenChange,
  organisationId,
  requisitions,
  vendors,
  policies,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  requisitions: Requisition[];
  vendors: Vendor[];
  policies: DirectPurchasePolicy[];
  onDone: () => void;
}) {
  const [requisitionId, setRequisitionId] = useState("");
  const [route, setRoute] = useState<
    "direct_purchase" | "external_purchase_order" | "emergency_purchase"
  >("direct_purchase");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const selected =
    requisitions.find((row) => row.id === requisitionId) || requisitions[0];
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const form = new FormData(event.currentTarget);
    const lines = (selected.requisition_lines || []).map((line) => ({
      requisition_line_id: line.id,
      quantity: Number(form.get(`quantity-${line.id}`)),
      unit_price: Number(form.get(`price-${line.id}`)),
    }));
    setBusy(true);
    setError("");
    try {
      const externalIssuedAt = String(form.get("externalIssuedAt") || "");
      await actions.createDirectPurchaseOrder(organisationId, {
        p_requisition_id: selected.id,
        p_vendor_id: form.get("vendor"),
        p_currency: String(form.get("currency") || "INR").toUpperCase(),
        p_exchange_rate_to_base: Number(form.get("exchangeRate")),
        p_lines: lines,
        p_route: route,
        p_reason: form.get("reason"),
        p_external_reference:
          route === "external_purchase_order"
            ? form.get("externalReference")
            : null,
        p_external_issued_at:
          route === "external_purchase_order" && externalIssuedAt
            ? new Date(externalIssuedAt).toISOString()
            : null,
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(780px,calc(100vw-2rem))]">
        <DialogHeader
          eyebrow="GOVERNED FAST LANE"
          title="Create a direct or external purchase order"
          description="Only an approved requisition within an active policy can skip competitive sourcing. The reason and source remain visible in the audit trail."
        />
        <form onSubmit={submit} className="grid max-h-[75vh] gap-5 overflow-auto p-6">
          {!policies.some((policy) => policy.active) && (
            <p className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
              No active direct-purchase policy exists. Finance or an administrator must configure a limit first.
            </p>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Approved requisition
              <Select
                name="requisition"
                value={selected?.id || ""}
                onChange={(event) => setRequisitionId(event.target.value)}
                required
              >
                {requisitions.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.request_no} · {row.title}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Purchase route
              <Select
                name="route"
                value={route}
                onChange={(event) => setRoute(event.target.value as typeof route)}
              >
                <option value="direct_purchase">Direct PO within policy</option>
                <option value="external_purchase_order">Record existing external PO</option>
                <option value="emergency_purchase">Emergency purchase</option>
              </Select>
            </Label>
            <Label>
              Vendor
              <Select name="vendor" required>
                {vendors.filter((vendor) => vendor.active).map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Currency
              <Input name="currency" defaultValue="INR" maxLength={3} pattern="[A-Za-z]{3}" required />
            </Label>
            <Label>
              Exchange rate to base currency
              <Input name="exchangeRate" type="number" min="0.00000001" step="0.00000001" defaultValue="1" required />
            </Label>
            {route === "external_purchase_order" && (
              <>
                <Label>
                  Original PO reference
                  <Input name="externalReference" required />
                </Label>
                <Label>
                  Original issue time
                  <Input
                    name="externalIssuedAt"
                    type="datetime-local"
                    defaultValue={localDateTimeInputValue()}
                    required
                  />
                </Label>
              </>
            )}
          </div>
          <div>
            <h3 className="text-sm font-bold text-slate-800">Approved requirement lines</h3>
            <div className="mt-2 grid gap-3">
              {(selected?.requisition_lines || []).map((line) => (
                <div key={line.id} className="grid gap-3 rounded-xl border border-slate-200 p-4 sm:grid-cols-[1fr_140px_160px]">
                  <span>
                    <strong className="block text-sm">{line.name}</strong>
                    <small className="text-slate-500">{line.quantity} {line.unit} · {titleCase(line.classification)}</small>
                  </span>
                  <Label>
                    Quantity
                    <Input
                      name={`quantity-${line.id}`}
                      type="number"
                      min="0.0001"
                      max={line.quantity}
                      step="0.0001"
                      defaultValue={line.quantity}
                      required
                    />
                  </Label>
                  <Label>
                    Unit price
                    <Input
                      name={`price-${line.id}`}
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={line.estimated_unit_price}
                      required
                    />
                  </Label>
                </div>
              ))}
            </div>
          </div>
          <Label>
            Why competitive sourcing is not being used
            <Textarea
              name="reason"
              minLength={10}
              maxLength={2000}
              placeholder="State the policy basis, urgency, contract, compatibility or external provenance."
              required
            />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !selected || !vendors.length || !policies.some((policy) => policy.active)}
            >
              {busy ? "Creating governed record…" : route === "external_purchase_order" ? "Record issued PO" : "Create draft PO"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DirectPurchasePolicyDialog({
  open,
  onOpenChange,
  organisationId,
  policies,
  units,
  locations,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  policies: DirectPurchasePolicy[];
  units: BusinessUnit[];
  locations: Location[];
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.configureDirectPurchasePolicy(organisationId, {
        p_policy_id: null,
        p_name: form.get("name"),
        p_max_total_base: Number(form.get("limit")),
        p_business_unit_id: form.get("unit") || null,
        p_location_id: form.get("location") || null,
        p_cost_category: form.get("category") || null,
        p_line_classification: form.get("classification") || null,
        p_allow_emergency: Boolean(form.get("allowEmergency")),
        p_active: true,
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(720px,calc(100vw-2rem))]">
        <DialogHeader
          eyebrow="PROCUREMENT POLICY"
          title="Direct purchase limits"
          description="Create narrow fast-lane rules. More specific operating-unit, location, category or classification policies take precedence."
        />
        <div className="max-h-[75vh] overflow-auto">
          {!!policies.length && (
            <div className="grid gap-2 border-b border-slate-100 p-6">
              {policies.map((policy) => (
                <div key={policy.id} className="flex justify-between gap-4 rounded-xl border border-slate-200 p-3 text-sm">
                  <span>
                    <strong className="block">{policy.name}</strong>
                    <small className="text-slate-500">
                      {policy.business_units?.name || policy.locations?.name || policy.cost_category || "Organisation-wide"}
                      {policy.line_classification ? ` · ${titleCase(policy.line_classification)}` : ""}
                    </small>
                  </span>
                  <span className="text-right">
                    <strong>{money(policy.max_total_base)}</strong>
                    <small className="block text-slate-500">{policy.allow_emergency ? "Emergency allowed" : "Standard direct only"}</small>
                  </span>
                </div>
              ))}
            </div>
          )}
          <form onSubmit={submit} className="grid gap-4 p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <Label>
                Policy name
                <Input name="name" placeholder="Small operating purchases" minLength={3} required />
              </Label>
              <Label>
                Maximum value in base currency
                <Input name="limit" type="number" min="0.01" step="0.01" required />
              </Label>
              <Label>
                Operating unit
                <Select name="unit">
                  <option value="">All operating units</option>
                  {units.map((unit) => (
                    <option key={unit.id} value={unit.id}>{unit.code} · {unit.name}</option>
                  ))}
                </Select>
              </Label>
              <Label>
                Location
                <Select name="location">
                  <option value="">All locations</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>{location.name}</option>
                  ))}
                </Select>
              </Label>
              <Label>
                Cost category
                <Input name="category" placeholder="Optional exact category" />
              </Label>
              <Label>
                CAPEX/OPEX classification
                <Select name="classification">
                  <option value="">Any classification</option>
                  <option value="capex">CAPEX</option>
                  <option value="opex">OPEX</option>
                  <option value="consumable">Consumable</option>
                  <option value="unclassified">Unclassified</option>
                </Select>
              </Label>
            </div>
            <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
              <input type="checkbox" name="allowEmergency" className="size-4" />
              Permit the emergency route under this value and scope
            </label>
            <FieldError>{error}</FieldError>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? "Saving policy…" : "Add direct-purchase policy"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PurchaseOrderCollaboration({
  organisationId,
  orders,
  messages,
  fulfilment,
  canReceive,
  onDone,
}: {
  organisationId: string;
  orders: PurchaseOrder[];
  messages: PurchaseOrderMessage[];
  fulfilment: SupplierFulfilmentUpdate[];
  canReceive: boolean;
  onDone: () => void;
}) {
  const [purchaseOrderId, setPurchaseOrderId] = useState(orders[0]?.id || "");
  const [message, setMessage] = useState("");
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState("");
  const thread = messages.filter(
    (row) => row.purchase_order_id === purchaseOrderId,
  );
  const pending = fulfilment.filter((row) => row.state === "submitted");
  if (!orders.length) return null;

  async function post(event: FormEvent) {
    event.preventDefault();
    if (!purchaseOrderId || !message.trim()) return;
    setBusy("message");
    try {
      await actions.postPurchaseOrderMessage(
        organisationId,
        purchaseOrderId,
        message.trim(),
      );
      setMessage("");
      onDone();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }

  async function decide(row: SupplierFulfilmentUpdate, decision: string) {
    const reason = reasons[row.id]?.trim();
    if (!reason) return;
    setBusy(row.id + decision);
    try {
      await actions.decideSupplierFulfilmentUpdate(
        organisationId,
        row.id,
        decision,
        reason,
      );
      onDone();
    } catch (error) {
      reportActionError(error);
    } finally {
      setBusy("");
    }
  }

  return (
    <section className="mt-6 grid gap-5 xl:grid-cols-2">
      <Panel>
        <h2 className="font-serif text-xl">Supplier conversation</h2>
        <p className="mt-1 text-sm text-slate-500">
          Keep order questions and commitments attached to the PO record.
        </p>
        <Select
          className="mt-4"
          value={purchaseOrderId}
          onChange={(event) => setPurchaseOrderId(event.target.value)}
        >
          {orders.map((row) => (
            <option key={row.id} value={row.id}>
              {row.po_no} · {row.vendors?.name}
            </option>
          ))}
        </Select>
        <div className="mt-4 max-h-64 space-y-2 overflow-auto">
          {thread.map((row) => (
            <div key={row.id} className="rounded-xl border border-slate-200 p-3">
              <div className="flex justify-between gap-3 text-xs text-slate-500">
                <strong>{titleCase(row.sender_kind)}</strong>
                <span>{displayDate(row.created_at, true)}</span>
              </div>
              <p className="mt-1 text-sm text-slate-700">{row.body}</p>
            </div>
          ))}
          {!thread.length && (
            <p className="text-sm text-slate-500">No messages on this order.</p>
          )}
        </div>
        <form className="mt-4 grid gap-2" onSubmit={post}>
          <Textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Ask a question or record an order commitment"
            maxLength={4000}
          />
          <Button disabled={busy === "message" || !message.trim()} type="submit">
            Send to supplier
          </Button>
        </form>
      </Panel>
      <Panel>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-serif text-xl">Supplier fulfilment inbox</h2>
            <p className="mt-1 text-sm text-slate-500">
              Supplier updates remain unaccepted until a receiver verifies them.
            </p>
          </div>
          <Badge value={pending.length ? "action required" : "current"} />
        </div>
        <div className="mt-4 grid gap-3">
          {pending.map((row) => {
            const po = orders.find(
              (item) =>
                item.id === row.commercial_instruments?.source_purchase_order_id,
            );
            return (
              <div key={row.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex justify-between gap-3">
                  <div>
                    <strong className="text-sm">
                      {po?.po_no || "Purchase order"} · {titleCase(row.kind)}
                    </strong>
                    <small className="block text-slate-500">
                      {row.details?.performed_by} · {displayDate(row.occurred_at, true)}
                    </small>
                  </div>
                  <Badge value={row.state} />
                </div>
                <p className="mt-2 text-sm text-slate-600">{row.details?.note}</p>
                {canReceive && (
                  <div className="mt-3 grid gap-2">
                    <Input
                      value={reasons[row.id] || ""}
                      onChange={(event) =>
                        setReasons((current) => ({
                          ...current,
                          [row.id]: event.target.value,
                        }))
                      }
                      placeholder="Verification decision reason"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!reasons[row.id]?.trim() || busy === row.id + "accept"}
                        onClick={() => decide(row, "accept")}
                      >
                        Accept evidence
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!reasons[row.id]?.trim() || busy === row.id + "reject"}
                        onClick={() => decide(row, "reject")}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!pending.length && (
            <p className="text-sm text-slate-500">
              No supplier-submitted fulfilment updates await review.
            </p>
          )}
        </div>
      </Panel>
    </section>
  );
}

function FulfilmentDialog({
  purchaseOrder,
  onOpenChange,
  organisationId,
  onDone,
}: {
  purchaseOrder?: PurchaseOrder;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!purchaseOrder) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await recordFulfilmentEvent(organisationId, purchaseOrder.id, {
        kind: String(form.get("kind")),
        occurredAt: new Date(String(form.get("occurredAt"))).toISOString(),
        reference: String(form.get("reference") || ""),
        performedBy: String(form.get("performedBy") || ""),
        note: String(form.get("note") || ""),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(purchaseOrder)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="OPERATIONAL FULFILMENT"
          title={`Record progress for ${purchaseOrder?.po_no || "order"}`}
          description="Record who performed the work and who verified it. Your signed-in identity is retained as the accountable recorder."
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Label>
              Fulfilment stage
              <Select name="kind" defaultValue="installation" required>
                <option value="inspection">Inspection</option>
                <option value="service_entry">Service delivery</option>
                <option value="milestone">Milestone</option>
                <option value="installation">Installation</option>
                <option value="commissioning">Commissioning</option>
                <option value="acceptance">Operational acceptance</option>
              </Select>
            </Label>
            <Label>
              When it occurred
              <Input
                name="occurredAt"
                type="datetime-local"
                defaultValue={localDateTimeInputValue()}
                required
              />
            </Label>
            <Label>
              Performed by / service provider
              <Input
                name="performedBy"
                placeholder="Person, team or supplier"
                maxLength={200}
                required
              />
            </Label>
            <Label>
              Evidence reference
              <Input
                name="reference"
                placeholder="Certificate, job sheet or ticket"
                maxLength={200}
              />
            </Label>
          </div>
          <Label>
            Completion and verification note
            <Textarea
              name="note"
              placeholder="What was completed, checked and accepted?"
              maxLength={2000}
              required
            />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Recording…" : "Record accountable event"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptDialog({
  purchaseOrder,
  onOpenChange,
  organisationId,
  onDone,
}: {
  purchaseOrder?: PurchaseOrder;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const openLines =
    purchaseOrder?.po_lines?.filter(
      (line) => Number(line.received_qty) < Number(line.quantity),
    ) || [];
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!purchaseOrder) return;
    const form = new FormData(event.currentTarget);
    const lines = openLines
      .map((line) => ({
        po_line_id: line.id,
        accepted_qty: Number(form.get(`accepted-${line.id}`)),
        rejected_qty: Number(form.get(`rejected-${line.id}`)),
        notes: String(form.get(`notes-${line.id}`) || ""),
      }))
      .filter((line) => line.accepted_qty + line.rejected_qty > 0);
    setBusy(true);
    setError("");
    try {
      await actions.recordReceipt(
        organisationId,
        purchaseOrder.id,
        String(form.get("notes") || ""),
        lines,
      );
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(purchaseOrder)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="GOODS RECEIPT"
          title={`Receive ${purchaseOrder?.po_no || "order"}`}
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-3 rounded-xl bg-slate-50 p-4">
            {openLines.map((line) => (
              <div
                key={line.id}
                className="grid gap-2 md:grid-cols-[1.3fr_.6fr_.6fr_1fr]"
              >
                <div>
                  <strong className="text-sm">{line.description}</strong>
                  <small className="block text-slate-500">
                    Balance {Number(line.quantity) - Number(line.received_qty)}{" "}
                    {line.unit}
                  </small>
                </div>
                <Input
                  name={`accepted-${line.id}`}
                  aria-label="Accepted quantity"
                  type="number"
                  min="0"
                  max={Number(line.quantity) - Number(line.received_qty)}
                  step="0.01"
                  defaultValue={
                    Number(line.quantity) - Number(line.received_qty)
                  }
                />
                <Input
                  name={`rejected-${line.id}`}
                  aria-label="Rejected quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  defaultValue="0"
                />
                <Input
                  name={`notes-${line.id}`}
                  aria-label="Line note"
                  placeholder="Line note"
                />
              </div>
            ))}
          </div>
          <Label>
            Receipt note
            <Textarea name="notes" />
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Post goods receipt
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
function InvoiceDialog({
  purchaseOrder,
  onOpenChange,
  organisationId,
  onDone,
}: {
  purchaseOrder?: PurchaseOrder;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!purchaseOrder) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.recordInvoice(organisationId, {
        p_purchase_order_id: purchaseOrder.id,
        p_invoice_no: form.get("invoiceNo"),
        p_invoice_date: form.get("invoiceDate"),
        p_amount: Number(form.get("amount")),
        p_tax_amount: Number(form.get("tax")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={Boolean(purchaseOrder)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="ACCOUNTS PAYABLE"
          title={`Record invoice for ${purchaseOrder?.po_no || "order"}`}
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Vendor
              <Input value={purchaseOrder?.vendors?.name || ""} readOnly />
            </Label>
            <Label>
              PO total
              <Input value={money(purchaseOrder?.total)} readOnly />
            </Label>
            <Label>
              Invoice number
              <Input name="invoiceNo" required />
            </Label>
            <Label>
              Invoice date
              <Input name="invoiceDate" type="date" required />
            </Label>
            <Label>
              Taxable amount
              <Input
                name="amount"
                type="number"
                min="0.01"
                step="0.01"
                defaultValue={purchaseOrder?.total}
                required
              />
            </Label>
            <Label>
              Tax amount
              <Input
                name="tax"
                type="number"
                min="0"
                step="0.01"
                defaultValue="0"
                required
              />
            </Label>
          </div>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Record invoice
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
